export const PERSONAL_FINANCES_SCHEMA = {
  en: {
    navigation: {
      segments: [
        { key: "overview", text: "Overview" },
        { key: "transactions", text: "Transactions" },
        { key: "recurring", text: "Recurring" },
        { key: "budgeting", text: "Budgeting" },
        { key: "spendings", text: "Spending" },
      ],
      institutions: {
        text: "Institutions",
        image_alt: "Institutions",
      },
      upgrade_plan: {
        text: "Upgrade Plan",
        image_alt: "Upgrade Plan",
      },
    },
    global: {
      see_all: "See all",
      previous: "Previous",
      next: "Next",
      refresh: "Refresh",
      refresh_in: "Refresh in: {countdown}s",
      actions: "Actions",
      saving: "Saving...",
      save_note: "Save Note",
      note_saved: "Note saved",
      add_note: "Add Note",
      uncategorized: "Uncategorized",
      other: "Other",
      account_types: {
        depository: {
          checking: "Checking Account",
          savings: "Savings Account",
          fallback: "Account",
        },
        credit: "Credit Card",
      },
    },
    status: {
      transactions: {
        no_data: {
          title: "Preparing Your Financial History",
          description:
            "Your accounts are connected successfully. We’re now retrieving your transaction history and building your financial insights. This usually takes a few moments.",
        },
        processing: {
          title: "Processing Your Financial History",
          description:
            "We’re currently processing transaction history from {processing_institutions}. Balances, spending trends, budgets, and other insights may appear incomplete until processing is finished. We’ll continue updating your data automatically.",
        },
      },
      recurring: {
        no_data: {
          title: "Identifying Recurring Activity",
          description:
            "We’re analyzing your transaction history to identify recurring bills, subscriptions, income, and other repeating activity. This process usually takes a few moments after your accounts are connected.",
        },
        processing: {
          title: "Processing Recurring Activity",
          description:
            "We’re analyzing transaction history from {processing_institutions} to identify recurring bills, subscriptions, income, and other repeating activity. Recurring insights will update automatically as processing continues.",
        },
      },
    },
    overview: {
      money_flow: {
        title: "Money Flow",
        current_balance: "Current Balance",
        spending_trend: "Spending",
        legends: {
          average_six_months: "Average - 6 Months",
          current_month: "Current Month",
          previous_month: "Previous Month",
        },
        growth: {
          up: "Up {percentage}% from {period}",
          down: "Down {percentage}% from {period}",
        },
      },
      latest_transactions: {
        title: "Recent",
        empty: "No transactions found",
      },
      upcoming_transactions: {
        title: "Upcoming",
        empty: "Nothing Upcoming",
        status: {
          today: "Today",
          tomorrow: "Tomorrow",
          in_days: "In {days} days",
        },
      },
      budgets: {
        title: "Budgets",
        empty_title: "You haven't created any budgets yet.",
        empty_description: "Create one to start tracking your spending goals.",
      },
    },
    transactions: {
      latest_title: "Recent",
      table_title: "All",
      search_placeholder: "Search by Merchant",
      filter_title: "Filter Transactions",
      headers: {
        merchant: "Merchant",
        category: "Category",
        description: "Description",
        amount: "Amount",
        date: "Date",
      },
      actions: {
        view_details: "View Details",
      },
      date_filters: {
        today: "Today",
        one_week: "1W",
        one_month: "1M",
        one_year: "1Y",
        custom: "Custom",
        modal: {
          title: "Custom Date Range",
          apply: "Filter",
          from: "From",
          to: "To",
          from_date: "From Date",
          to_date: "To Date",
          error_field: "Date Range",
          validation: {
            both_dates_required: "Both dates are required.",
            invalid_dates: "Please enter valid dates.",
            invalid_order: "From date must be before or equal to To date.",
          },
        },
      },
      details: {
        fallback_title: "Transaction",
        pending_status: "Pending",
        completed_status: "Completed",
        tax_deductible: "Tax Deductible?",
        calculator_icon_alt: "Calculator Icon",
        marked_tax_deductible: "Marked as tax deductible",
        unmarked_tax_deductible: "Unmarked as tax deductible",
        unable_to_update_tax_deductible:
          "Unable to update tax deductible status.",
        unable_to_save_note: "Unable to save note.",
      },
      empty: "No transactions found",
    },
    recurring: {
      recurrent_type_labels: {
        bill: "Bill",
        subscription: "Subscription",
        payment: "Payment",
        income: "Income",
        transfer: "Transfer",
        loan_payment: "Loan Payment",
        frequent_merchant: "Frequent Merchant",
        other: "Other",
      },
      frequency_options: {
        weekly: "Weekly",
        biweekly: "Biweekly",
        semi_monthly: "Semi Monthly",
        monthly: "Monthly",
        annually: "Yearly",
        unknown: "Unknown",
      },
      upcoming: {
        title: "Upcoming",
        expected_spending: "Expected Spending",
        expected_income: "Expected Income",
        spending_tab: "Spending",
        income_tab: "Income",
        empty: "Nothing Upcoming",
      },
      table_title: "All",
      search_placeholder: "Search by Merchant",
      filter_title: "Filter Transactions",
      headers: {
        merchant: "Merchant",
        description: "Description",
        category: "Category",
        type: "Type",
        next_bill: "Next Bill",
        frequency: "Frequency",
      },
      actions: {
        view_edit: "View & Edit",
      },
      details: {
        payment_history: "Payment History",
        close_recurrent_type_menu: "Close recurring type menu",
        recurrent_type_aria: "Recurring type: {type}",
        recurring_type_saved: "Recurring type saved",
        unable_to_save_recurring_type: "Unable to save recurring type.",
        unable_to_save_note: "Unable to save note.",
      },
      empty: "No recurring transactions found",
    },
    budgeting: {
      overall_usage: {
        title: "Overview",
        summary: "{spent} spent from {limit} limit",
        empty_insight: {
          title: "Establish Your Financial Guardrails",
          description:
            "Setting up target budgets allows you to frame concrete milestone targets, curb unnecessary spending leakages, and build structured, predictable runway metrics aligned with your core financial goals.",
          cta: "Create Your First Budget",
        },
        stats: {
          total_limit: "Limit",
          total_limit_tooltip_title: "Total Budget Limit",
          total_limit_tooltip_description:
            "This is the total amount you've budgeted across all active budgets for the current period.",
          remaining: "Remaining",
          remaining_tooltip_title: "Remaining Budget",
          remaining_tooltip_description:
            "The amount still available before reaching your combined budget limits.",
          spent: "Spent",
          spent_tooltip_title: "Total Spent",
          spent_tooltip_description:
            "You've spent {spent_amount} across all active budgets. You'll receive an alert when spending reaches 90% of your total budget limit.",
        },
      },
      budgets: {
        title: "Budgets",
        create_button: "Create Budget",
        empty_title: "You haven't created any budgets yet.",
        empty_description: "Create one to start tracking your spending goals.",
        filtered_empty: "No budgets match your filters",
        headers: {
          name: "Name",
          description: "Description",
          categories: "Categories",
          frequency: "Frequency",
          spent_vs_limit: "Budget Usage",
        },
        actions: {
          detail: "Budget Detail",
          delete: "Delete Budget",
        },
        spent_limit_tooltips: {
          category_spending_title: "Category Spending",
          category_spending_description:
            "You've spent {category_amount} in this category so far this month. Last month, you spent {previous_category_amount}.",
          current_spending_title: "Current Spending",
          current_spending_description:
            "The total amount spent during the current month.",
          budget_limit_title: "Budget Limit",
          budget_limit_description:
            "You've set a spending limit of {budget_limit} for this budget.",
        },
      },
      create_budget: {
        title: "Create Budget",
        budget_name: "Budget Name",
        budget_name_placeholder: "e.g., Family Vacation",
        frequency: "Frequency",
        description: "Description",
        description_placeholder: "What is this budget for?",
        limit: "Limit ($)",
        threshold: "Alert Threshold (%)",
        tracking_sources: "What should we track?",
        institutions: "Institutions",
        accounts: "Accounts",
        categories: "Categories",
        select_institutions: "Select Institutions",
        select_accounts: "Select Accounts",
        select_categories: "Select Categories",
        submit: "Create Budget",
        creating: "Creating...",
        errors: {
          create_failed: "Could not create budget",
          negative_limit: "Limit cannot be negative.",
          select_category: "Select at least one spending category.",
        },
      },
      frequency_options: {
        weekly: "Weekly",
        monthly: "Monthly",
      },
      threshold_options: {
        fifty: "50%",
        seventy_five: "75%",
        ninety: "90%",
      },
      filter: {
        title: "Filter Budgets",
        institution: "Institution",
        account: "Account",
        category: "Category",
        frequency: "Frequency",
        all_frequencies: "All Frequencies",
      },
      delete_budget: {
        title: "Delete Budget",
        message:
          "Are you sure you want to delete this budget? You will lose all the progress.",
        confirm: "Delete",
        cancel: "Cancel",
      },
      details: {
        current_progress: "Current Progress",
        status: "Status",
        settings: "Budget Settings",
        institutions: "Institutions",
        accounts: "Accounts",
        categories: "Categories",
        frequency: "Frequency",
        limit_threshold: "Limit & Threshold",
        limit_threshold_value: "Limit: {limit} | Threshold: {threshold}%",
        all_items: "All {type}",
        institution_icon_alt: "Institutions Icon",
      },
    },
    spendings: {
      review_required: {
        title: "Review Required",
        message:
          "You have {count} transactions this month that might count toward your spending totals.",
        action_text: "Review Now",
      },
      spending_balance: {
        title: "Trend",
        growth: {
          up: "Up {percentage}% from {period}",
          down: "Down {percentage}% from {period}",
        },
      },
      breakdown: {
        title: "Breakdown",
        all: "All",
        by_category: "By Category",
        by_account: "By Account",
      },
      heavy_hitters: {
        title: "Top Spending Categories",
        description_with_period: "Biggest spending categories in {period}.",
        description: "Biggest spending categories.",
        empty: "No spending categories found.",
        actions: {
          see_transactions: "See Transactions",
        },
        modal_title: "{period} > Heavy Transactions > {category}",
        headers: {
          category: "Category",
          spent: "Spent",
        },
        spent_tooltips: {
          category_spending_title: "Category Spending",
          category_spending_description:
            "Shows how much you've spent in this category during the current month compared to last month.",
          current_spending_title: "Current Spending",
          current_spending_description:
            "The total amount spent during the current month.",
        },
      },
    },
    institutions: {
      modal: {
        title: "Institutions",
        icon_alt: "Institution Icon",
        connect: "Connect",
        connecting: "Connecting...",
        empty_title: "Connect Your Financial Institution",
        empty_description:
          "To unlock real-time financial tracking, automated weekly budget cycles, and insights into your spending habits, connect your bank account securely using Plaid.",
        connection_error:
          "We could not finish connecting this institution. Please try again.",
        link_closed_error:
          "Plaid Link was closed before the institution connected.",
        start_link_error:
          "We could not start Plaid Link. Please try again in a moment.",
        missing_uuid_error:
          "This institution payload is missing uuid. Please refresh and try again.",
        start_institution_link_error:
          "We could not start Plaid Link for this institution. Please try again.",
      },
      item: {
        disconnected: "Disconnected",
        toggle_alt: "Toggle institution details",
        missing_id_error: "Missing institution id.",
        action_failed_error: "Action failed. Please try again.",
        opening_plaid: "Opening Plaid...",
        reconnect: "Reconnect",
        add_account: "Add Account",
        cancel: "Cancel",
        actions: {
          unlink: {
            label: "Unlink Institution",
            title: "Unlink Institution",
            confirm: "Unlink",
            loading: "Unlinking...",
            message:
              "This will disconnect the institution from Plaid and stop future updates. Existing historical data will be kept.",
          },
          remove: {
            label: "Remove Institution",
            title: "Remove Institution",
            confirm: "Remove",
            loading: "Removing...",
            message:
              "This will disconnect the institution and permanently remove its accounts, transactions, recurring streams, and history. This action cannot be undone.",
          },
        },
      },
      account_subtype_fallback: "Account",
      unknown_merchant: "Unknown",
      group_labels: {
        depository: "Cash & Savings",
        credit: "Credit Cards",
        loan: "Loans",
        other: "Other Accounts",
      },
      tooltips: {
        card_balance: {
          title: "Card Balance",
          description: "The current balance owed on this credit card.",
          no_limit_description:
            "The balance currently owed on this card. A credit limit was not provided by the financial institution.",
        },
        card_limit: {
          title: "Card Limit",
          description: "The total credit limit available on this card.",
        },
        credit_used: {
          title: "Credit Used",
          description:
            "The total credit currently being used across all connected credit cards at this institution.",
        },
        total_credit_limit: {
          title: "Total Credit Limit",
          description:
            "The combined credit limit across all connected credit cards at this institution.",
        },
        high_global_utilization: {
          title: "High Credit Utilization",
          description:
            "Your credit utilization at this institution is currently {percentage}%. Keeping utilization below 30% may help support a healthier credit profile.",
        },
        high_card_utilization: {
          title: "High Utilization Alert",
          description:
            "This individual card is at {percentage}% utilization. Exceeding a 30% threshold may negatively impact your credit score across many financial institutions.",
        },
        available_cash_global: {
          title: "Available Cash",
          description:
            "Total cash available across your checking and savings accounts. Credit cards, loans, and other debt accounts are not included.",
        },
        available_cash_institution: {
          title: "Available Cash",
          description:
            "Total cash available across all checking and savings accounts connected to this institution.",
        },
        aggregated_loan_debt: {
          title: "Aggregated Loan Debt",
          description:
            "Total outstanding debt across all loan accounts from this institution.",
        },
        healthy_utilization: {
          title: "Healthy Utilization Indicator",
          description:
            "This card is using {percentage}% of its available credit. Keeping utilization below 30% is generally considered healthy and may help support a stronger credit profile.",
        },
      },
    },
  },
};
